import { UserPreferences } from '../../store/userPreferences/type'

export type LastLookChoice =
  | 'PopupAndActivityFeed'
  | 'InActivityFeed'
  | 'AutomaticallyAccept'

const DEFAULT_START_TIME = '09:00'

/*
    The raw input text is kept alongside the checkbox because neither a
    part-typed value nor a checked-but-blank field can be held in the number
    the preference is stored as.
*/
interface OptionalNumber {
  enabled: boolean
  text: string
}

/* Each optional number is switched off by a fixed value, differing by field. */
const offValues = {
  minimumTradeSize: 0,
  maximumTradeSize: null,
  defaultBidOfferValue: null,
  tobPriceIncrement: null,
  tobSpreadIncrement: null
} as const

export type OptionalField = keyof typeof offValues
type BooleanField = 'muteNotifications' | 'cancelBalanceUponPartialTrade'

type WorkingPreferences = Record<OptionalField, OptionalNumber> &
  Record<BooleanField, boolean> & {
    isChanged: boolean
    openfinAutoStart: boolean
    openfinStartTime: string
    lastLookChoice: LastLookChoice
    defaultSpotHedgePreference: string
  }

export type Action =
  | { type: 'toggle'; field: BooleanField }
  | { type: 'toggleOptional'; field: OptionalField }
  | { type: 'setOptionalText'; field: OptionalField; text: string }
  | { type: 'toggleAutoStart' }
  | { type: 'setStartTime'; time: string }
  | { type: 'setLastLook'; choice: LastLookChoice }
  | { type: 'setSpotHedge'; value: string }

// none of these preferences is meaningful at 0 or below, so anything else is off
const isSet = (value: number | null) => typeof value === 'number' && value > 0

const optionalOff: OptionalNumber = { enabled: false, text: '' }

const toOptionalNumber = (value: number | null): OptionalNumber =>
  isSet(value) ? { enabled: true, text: String(value) } : optionalOff

// a hedge-prefixed value like 'HT' selects Auto-Hedge but is saved as-is
export const spotHedgeSelection = (preference: string) =>
  preference.startsWith('H') ? 'H' : preference

export const initWorkingPreferences = (
  stored: UserPreferences
): WorkingPreferences => ({
  isChanged: false,
  muteNotifications: stored.muteNotifications,
  cancelBalanceUponPartialTrade: stored.cancelBalanceUponPartialTrade,
  minimumTradeSize: toOptionalNumber(stored.minimumTradeSize),
  maximumTradeSize: toOptionalNumber(stored.maximumTradeSize),
  defaultBidOfferValue: toOptionalNumber(stored.defaultBidOfferValue),
  tobPriceIncrement: toOptionalNumber(stored.tobPriceIncrement),
  tobSpreadIncrement: toOptionalNumber(stored.tobSpreadIncrement),
  openfinAutoStart: stored.openfinAutoStart,
  openfinStartTime: stored.openfinStartTime,
  lastLookChoice: stored.askForOrderConfirmation
    ? stored.lastLookInPopup
      ? 'PopupAndActivityFeed'
      : 'InActivityFeed'
    : 'AutomaticallyAccept',
  // coerced here so a save repairs a blank, and the hub payload is guarded once
  defaultSpotHedgePreference: stored.defaultSpotHedgePreference || 'S'
})

const applyAction = (
  preferences: WorkingPreferences,
  action: Action
): WorkingPreferences => {
  switch (action.type) {
    case 'toggle':
      return { ...preferences, [action.field]: !preferences[action.field] }
    case 'toggleOptional':
      return {
        ...preferences,
        [action.field]: {
          ...preferences[action.field],
          enabled: !preferences[action.field].enabled
        }
      }
    case 'setOptionalText':
      return {
        ...preferences,
        [action.field]: { ...preferences[action.field], text: action.text }
      }
    case 'toggleAutoStart': {
      const openfinAutoStart = !preferences.openfinAutoStart
      return {
        ...preferences,
        openfinAutoStart,
        openfinStartTime: openfinAutoStart ? DEFAULT_START_TIME : ''
      }
    }
    case 'setStartTime':
      return { ...preferences, openfinStartTime: action.time }
    case 'setLastLook':
      return { ...preferences, lastLookChoice: action.choice }
    case 'setSpotHedge':
      return { ...preferences, defaultSpotHedgePreference: action.value }
  }
}

export const reducer = (
  preferences: WorkingPreferences,
  action: Action
): WorkingPreferences => ({
  ...applyAction(preferences, action),
  isChanged: true
})

const savedNumber = <TOff extends number | null>(
  { enabled, text }: OptionalNumber,
  offValue: TOff
) => {
  const entered = Number(text)
  return enabled && isSet(entered) ? entered : offValue
}

export const toPreferences = (
  stored: UserPreferences,
  working: WorkingPreferences
): UserPreferences => ({
  ...stored,
  muteNotifications: working.muteNotifications,
  cancelBalanceUponPartialTrade: working.cancelBalanceUponPartialTrade,
  minimumTradeSize: savedNumber(
    working.minimumTradeSize,
    offValues.minimumTradeSize
  ),
  maximumTradeSize: savedNumber(
    working.maximumTradeSize,
    offValues.maximumTradeSize
  ),
  defaultBidOfferValue: savedNumber(
    working.defaultBidOfferValue,
    offValues.defaultBidOfferValue
  ),
  tobPriceIncrement: savedNumber(
    working.tobPriceIncrement,
    offValues.tobPriceIncrement
  ),
  tobSpreadIncrement: savedNumber(
    working.tobSpreadIncrement,
    offValues.tobSpreadIncrement
  ),
  openfinAutoStart: working.openfinAutoStart,
  openfinStartTime: working.openfinStartTime,
  lastLookInPopup: working.lastLookChoice === 'PopupAndActivityFeed',
  askForOrderConfirmation: working.lastLookChoice !== 'AutomaticallyAccept',
  defaultSpotHedgePreference: working.defaultSpotHedgePreference
})
